/**
 * Shared header/footer/state components for generic partner sample-request pages.
 *
 * The legacy Medvantx pages duplicate these blocks inline. To avoid touching
 * Medvantx code we re-implement them here for the generic side; the existing
 * page-prefixed CSS files (verify-phone-*, address-selection-*, etc.) are
 * reused by passing a `prefix` prop, so no new CSS is introduced.
 */

import React from "react";
import { Button, Spin, Typography } from "antd";
import { Link } from "react-router-dom";
import DocUpdateIcon from "../../../assets/DocUpdateIcon.svg";
import WPFooter from "../../../components/WPFooter/WPFooter";

interface PrefixProps {
  /** CSS class prefix used by the parent page (e.g. "verify-phone", "sample-request"). */
  prefix: string;
}

export const DocUpdateHeader: React.FC<PrefixProps> = ({ prefix }) => (
  <header className={`${prefix}-header`}>
    <Link to="https://www.docupdate.io/">
      <img src={DocUpdateIcon} alt="DocUpdate" className={`${prefix}-logo`} />
    </Link>
  </header>
);

// Renders the shared WordPress footer (logo, nav, social, all accreditation
// badges, and legal links). `prefix` is accepted for backwards compatibility
// with existing call sites but is no longer used.
export const DocUpdateFooter: React.FC<PrefixProps> = () => <WPFooter />;

const centeredColumn: React.CSSProperties = {
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "center",
  minHeight: "50vh",
};

interface LoadingStateProps extends PrefixProps {
  /** Optional CSS class for the <main> wrapper. Defaults to `${prefix}-main`. */
  mainClassName?: string;
  message?: string;
}

export const LoadingState: React.FC<LoadingStateProps> = ({
  prefix,
  mainClassName,
  message = "Loading...",
}) => (
  <div className={`${prefix}-container`}>
    <DocUpdateHeader prefix={prefix} />
    <main className={mainClassName ?? `${prefix}-main`}>
      <div style={centeredColumn}>
        <Spin size="large" />
        <Typography.Text style={{ marginTop: 16, color: "#6b7280" }}>{message}</Typography.Text>
      </div>
    </main>
  </div>
);

interface ErrorStateProps extends PrefixProps {
  mainClassName?: string;
  error: string;
  showRetry?: boolean;
}

export const ErrorState: React.FC<ErrorStateProps> = ({
  prefix,
  mainClassName,
  error,
  showRetry = true,
}) => (
  <div className={`${prefix}-container`}>
    <DocUpdateHeader prefix={prefix} />
    <main className={mainClassName ?? `${prefix}-main`}>
      <div style={centeredColumn}>
        <Typography.Text style={{ color: "#ef4444", fontSize: 16 }}>{error}</Typography.Text>
        {showRetry && (
          <Button type="primary" onClick={() => window.location.reload()} style={{ marginTop: 16 }}>
            Try Again
          </Button>
        )}
      </div>
    </main>
  </div>
);
